import { useState, useEffect } from 'react';
import {
    Button,
    Container,
    Media,
    PageBanner,
    PageHeader,
} from '~/Common';
import { DashboardLayout } from '~/Layouts';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { Badge, Box, Flex, Grid, Heading, NavLink, Spinner, Text } from 'theme-ui';
import { getCategoriesAsync, getCategoryCompanies, getCategoryCompaniesAsync, getCurrentCategoryAsync } from 'features/category/categorySlice';
import { Card } from '~/Companies';
import { Card as DealCard } from '~/Deals';
import { FeaturedCompany } from '~/Industries/FeaturedCompany/Featured';
import { AsxAnnouncements } from '~/Industries/AsxAnnouncements';
import { List } from '~/Deals';
import { Icon } from 'assets/Icon';
import { IndustriesViewSkeleton } from './skeleton';
import { useResponsiveValue } from '@theme-ui/match-media';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { ResearchCard } from '~/Research';

const mapStateToProps = (state) => {
    return { industries: state.categories, theme: state.theme };
}

const IndustriesView = ({ industries, theme, match }) => {
    const { t } = useTranslation();
    useDocumentTitle(t('Industry Insights'));
    const dispatch = useDispatch();
    const [industrySlug, setIndustrySlug] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showMoreLoading, setShowMoreLoading] = useState(false);
    const isMobile = useResponsiveValue([true, true, true, false]);

    const industry = industries?.currentCategory;
    const randomFeaturedCompany = industry?.featured_companies[Math.floor(Math.random() * industry?.featured_companies.length)];
    const announcementsData = industry?.asx_announcements ? {
        title: 'Recent Announcement Summaries',
        announcements: industry?.asx_announcements.slice(0, 4).map((announcement) => ({
            announcement: announcement?.url,
            label: announcement?.title,
            priceSensitive: announcement?.price_sensitive,
            summary: announcement?.summary,
            releaseDate: announcement?.released_at
        }))
    } : null;

    useEffect(() => {
        // Get the current industry (if the slug is empty or has changed)
        if (industries.currentCategoryStatus === 'idle' || (match?.params?.slug !== industrySlug)) {
            setLoading(true);
            const newSlug = match?.params?.slug;

            setIndustrySlug(newSlug);
            dispatch(getCurrentCategoryAsync(newSlug));

            window.addEventListener('notFound', event => {
                setNotFound(true);
            });
        }
        if (industries?.currentCategoryStatus === 'attempted') {
            setLoading(false);
        }

        if (industries?.categories?.status === 'idle') {
            dispatch(getCategoriesAsync());
        }
    }, [industries, match, dispatch, industrySlug]);

    const buttonStyles = {
        marginX: 'auto',
        width: 'fit-content',
        height: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
        py: '14px',
        px: '24px',
        lineHeight: 1
    };

    const sliderSettings = {
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        centerMode: true,
        centerPadding: '20px',
    };


    const handleShowMore = () => {
        if (industry?.companies_pagination?.current_page >= industry?.companies_pagination?.last_page) {
            return;
        }

        setShowMoreLoading(true);
        const payload = getCategoryCompanies(industrySlug, { params: { page: industry?.companies_pagination.current_page + 1 }});
        console.log('payload', payload);
        dispatch(getCategoryCompaniesAsync(payload));
        setShowMoreLoading(false);
    }; 

    return (
        <DashboardLayout dark={true} hideCta={true} p={0}>
            {loading ? <IndustriesViewSkeleton /> : (
                <>
                    <PageBanner img={'industries'}>
                        <PageHeader sx={{ position: 'relative' }}>
                            <Container variant="layouts.default">
                                <Grid sx={{
                                    position: 'relative',
                                    gridGap: [2, 2, 2, 5],
                                    gridTemplateColumns: ['1fr', null, null, '2fr 3fr', 'auto 780px'],
                                    width: '100%'
                                }}>
                                    <Box sx={{ position: 'relative' }}>

                                        {notFound ? (
                                            <Box mb={4}>
                                                <Heading as="h1" variant="display" color="white" mb={2}>{t('companies.notFound.title')}</Heading>
                                                <Text as="p" color="white">{t('companies.notFound.description')}</Text>
                                            </Box>
                                        ) : (
                                            <>
                                                <Box mb={4}>
                                                    <Flex sx={{ justifyContent: 'left' }}>
                                                        <Badge mt={4} mx={0} px={3} variant="header" >{t('Industries')}</Badge>
                                                        <Badge mt={4} ml={3} px={3} variant="header" >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', paddingX: '6px' }}>
                                                                <Icon icon="stack" size="16" />
                                                                <Text sx={{
                                                                minWidth: '20px',
                                                                textAlign: 'center',
                                                                fontSize: 2,
                                                                letterSpacing: '-0.01em',
                                                                lineHeight: '25px',
                                                                px: 2
                                                                }}>{industry?.deals_count ?? 0 } current deals</Text>
                                                            </Box>
                                                        </Badge>
                                                    </Flex>
                                                    <Heading as="h1" variant="display" sx={{ lineHeight: '110%', fontWeight: 500, fontSize: '48px' }} mt={4} mb={3}>{t(industry?.name)}</Heading>
                                                    {industry?.email_summary ? <Text as="p" variant="h4" mb={3} color='darker'>{t(industry.email_summary)}</Text> : null}
                                                </Box>
                                                <Box mt={5}>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                    {industry?.video || industry?.image?.original ? (
                                        <Box sx={{
                                            maxWidth: ['none', 'none', 'none', '820px'],
                                            position: 'relative',
                                        }}>
                                            <Media
                                                radius={isMobile ? "16px" : undefined}
                                                ratio={isMobile ? 16 / 12 : undefined}
                                                video={industry?.video}
                                                image={industry?.image?.original}
                                            />
                                        </Box>
                                    ) : null}
                                </Grid>
                            </Container>
                        </PageHeader>
                    </PageBanner>
                    <Container sx={{ position: 'relative', zIndex: 1 }} variant="layouts.default">
                        <Grid>
                            {industry?.sub_categories?.length > 0 ? (
                                <Flex sx={{ justifyContent: 'center', flexDirection: 'column', textAlign: 'center' }}>
                                    <Badge mt={[-3, -3, -3, 3]} px={3} mb={4} variant="header" >{t('Industry Breakdown')}</Badge>
                                    <Flex sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: [2, 2, 2, 5], flexDirection: ['column', 'column', 'column', 'row'], mx: 'auto', width: '70%' }}>
                                        {industry?.sub_categories?.map((subCategory, index) => (
                                            <Box key={index} sx={{ textAlign: 'center', width: '150px', mx: 'auto' }}> 
                                                <Text as="h1" sx={{ color: 'darker', fontSize: '70px' }}>{t(subCategory.company_count)}</Text>
                                                <Heading as="h3" variant="h4" color='darker' sx={{ fontWeight: 400 }}>{t(subCategory.name)}</Heading>
                                            </Box>
                                        ))}
                                    </Flex>
                                </Flex>
                            ) : null}

                            {/* About section */}
                            {industry?.about_headline ? (
                                <>
                                    <Badge mt={[4, 4, 4, 5]} px={3} variant="header" >{t('About the Industry')}</Badge>
                                    <Box mt={4} sx={{ textAlign: 'center' }}>
                                        <Heading as="h3" variant="h1" sx={{ fontWeight: 400, mx: [2,2,2,5] }}>{t(industry?.about_headline)}</Heading>
                                        <Text as="p" variant="h5" mt={4} sx={{ fontWeight: 300, mx: [2,2,2,6] }} >{t(industry?.about_description)}</Text>
                                    </Box>
                                </>
                            ) : null}

                            {/* Featured Company */}
                            {randomFeaturedCompany ? (
                            <>
                                <Heading as="h1" variant="industryH2" mt={[5,5,5,6]} mb={3}>{t(`Featured ${industry?.name} Company`)}</Heading>
                                <FeaturedCompany company={randomFeaturedCompany} />
                            </>
                            ) : null}

                            {/* ASX Announcements */}
                            {industry?.asx_announcements?.length > 0 ? (
                                <Grid mt={[5, 5, 5, 6]} mb={3} sx={{
                                    position: 'relative',
                                    gridGap: [1, 1, 1, 5],
                                    gridTemplateColumns: ['1fr', '1fr', '1fr', '300px auto', 'auto 980px'],
                                    width: '100%'
                                }}>
                                    <Heading as="h1" variant="industryH2" >{t(`Latest Industry Announcements`)}</Heading>
                                    <AsxAnnouncements data={announcementsData} />
                                </Grid>
                            ) : null}

                            {/* Research */}
                            {industry?.research?.length > 0 ?
                                <>
                                    <Heading as="h1" variant="industryH2" mt={[4, 4, 4, 5]}>{t(`Latest Industry Research`)}</Heading>
                                    {isMobile ? (
                                        <Box >
                                            {industry?.research?.length > 0 ? (
                                                <Box sx={{
                                                    py: 2,
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    marginBottom: '40px',
                                                }}
                                                >
                                                    <Slider {...sliderSettings}>
                                                        {industry?.research?.map((report, key) => (
                                                            <Box key={`category-result${key}`}>
                                                                <ResearchCard report={report} company={report?.company} />
                                                            </Box>
                                                        ))}
                                                    </Slider>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    ) : (
                                        <Grid sx={{
                                            gridGap: 4,
                                            gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)'],
                                            gridTemplateRows: '1fr',
                                            width: '100%',
                                            overflowX: 'auto',
                                            pt: '10px',
                                            pb: '30px',
                                            px: 3,
                                            mx: -3,
                                            boxSizing: 'content-box',
                                        }}>
                                            {industry?.research?.map((report, key) => (
                                                <Box key={`category-research${key}`}>
                                                    <ResearchCard report={report} company={report?.company} variant="researchDashboard" />
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            : null}

                            {/* News */}
                            {/* {industry?.articles?.length > 0 ?
                                <>
                                    <Heading as="h1" variant="industryH2" mt={[4, 4, 4, 5]}>{t(`Latest Industry News`)}</Heading>
                                    {isMobile ? (
                                        <Box >
                                            {industry?.articles?.length > 0 ? (
                                                <Box sx={{
                                                    py: 2,
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    marginBottom: '40px',
                                                }}
                                                >
                                                    <Slider {...sliderSettings}>
                                                        {industry?.articles?.map((article, key) => (
                                                            <Box key={`category-article${key}`}>
                                                                <ArticleCard article={article} />
                                                            </Box>
                                                        ))}
                                                    </Slider>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    ) : (
                                        <Grid sx={{
                                            gridGap: 4,
                                            gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)'],
                                            gridTemplateRows: '1fr',
                                            width: '100%',
                                            overflowX: 'auto',
                                            pt: '10px',
                                            pb: '30px',
                                            px: 3,
                                            mx: -3,
                                            boxSizing: 'content-box',
                                        }}>
                                            {industry?.articles?.map((article, key) => (
                                                <Box key={`category-result${key}`}>
                                                    <ArticleCard article={article} />
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            : null} */}

                            {/* Deals */}
                            <Heading as="h1" variant="industryH2" mt={5}>{t(`Current Industry Deals`)}</Heading>
                            <Container variant="layouts.default">
                                <Flex sx={{
                                    flexDirection: 'column',
                                    position: 'relative',
                                }}>
                                    {industry?.deals?.length > 0 ?
                                        <>
                                            <Grid sx={{
                                                gridGap: 4,
                                                gridTemplateColumns: theme.itemLayout === 'grid'
                                                    ? ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)']
                                                    : '1fr',
                                                gridTemplateRows: '1fr',
                                                width: '100%',
                                                overflowX: 'auto',
                                                pt: '10px',
                                                pb: '30px',
                                                px: 3,
                                                mx: -3,
                                                boxSizing: 'content-box',
                                            }}>
                                                {industry?.deals?.map((deal, key) => (
                                                    <Box key={`deals-live-result${key}`}>
                                                        {theme.itemLayout === 'grid' ? (
                                                            <DealCard deal={deal} />
                                                        ) : (
                                                            <List deal={deal} />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Grid>
                                        </>
                                    : null}
                
                                    {(!loading && industry?.deals?.length === 0) &&
                                        <Box px={2}>
                                            <Flex sx={{
                                                py: 5,
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column'
                                            }}>
                                                <Icon icon={'lock-thin'} color="tealDark" mb={'28px'} width="84px" />
                                                <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('deals.live.notFoundHeading')}</Heading>
                                                <Text mt={1} variant="subtext">{t('deals.live.notFound')}</Text>
                                            </Flex>
                                        </Box>
                                    }
                                </Flex>        
                            </Container>

                            {/* Companies */}
                            <Heading as="h1" variant="industryH2" mt={5}>{t(`Explore ${industry?.name} Companies`)}</Heading>
                            {industry?.companies?.length > 0 ?
                                <>
                                    <Grid sx={{
                                        gridGap: 4,
                                        gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
                                        gridTemplateRows: '1fr',
                                        width: '100%',
                                        overflowX: 'auto',
                                        pt: '10px',
                                        pb: '30px',
                                        px: 3,
                                        mx: -3,
                                        boxSizing: 'content-box',
                                    }}>
                                        {industry?.companies?.map((company, key) => (
                                            <Box key={`category-result${key}`}>
                                                <Card company={company} showDeals={false} />
                                            </Box>
                                        ))}
                                    </Grid>

                                    {(!showMoreLoading && industry?.companies?.length < industry?.companies_pagination?.total) && (
                                        <Button onClick={handleShowMore} variant="primary" sx={buttonStyles} mr={3} >{t('Show me more')}</Button>
                                    )}

                                    {showMoreLoading && (
                                        <Flex sx={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%'
                                        }}>
                                            <Spinner variant="styles.spinner" m={'auto'} />
                                        </Flex>
                                    )}
                                </>
                            : null}

                            {/* Industries */}
                            <Heading as="h1" variant="industryH2" mt={5}>{t(`Explore Industries`)}</Heading>
                            <Flex mt={3} sx={{ gap: '22px', flexWrap: 'wrap', overflowX: isMobile ? 'scroll' : 'auto', maxWidth: '100vw' }}>
                                {industries?.categories?.results?.map((category, key) => (
                                    <Box
                                        as={NavLink}
                                        href={`/industries/${category.slug}`}
                                        sx={{
                                            paddingY: '0px',
                                            backgroundColor: '#00000A10',
                                            borderRadius: '16px',
                                            width: 'auto',
                                            flexShrink: 0,
                                            '&:hover': {
                                                backgroundColor: '#00000A20',
                                            },
                                        }} 
                                        key={`category-result${key}`}
                                    >
                                        <Box py="20px" px="20px"  sx={{ color: '#00000A', fontWeight: 'normal' }}>
                                            {category.name}
                                        </Box>
                                    </Box>
                                ))}
                            </Flex>
                        </Grid>
                    </Container>
                </>
            )}
        </DashboardLayout>
    );
};

export default connect(mapStateToProps, null)(IndustriesView);